/*
 * ===========================================================================
 * [StrongGate Limited] [http://www.stronggate.com]
 * Copyright (c) 2025. All rights Reserved.
 * Unauthorized access, use, dissemination, distribution, reproduction of this
 * file, or a portion of, in any form is prohibited and unlawful. If you receive
 * this file, and you are not an authorized company employee, or you believe you
 * have received this file in error, please notify the sender (or originator)
 * and promptly delete this file.
 *
 * Project:
 * File: ProfileData.ts
 * Created by: chan.ying
 * Create Date: 2025-07-19 13:57
 * Version Number: 1.0
 * Version Description:
 *
 * =============================================================================
 */

import OnlineUserManager from "@/security/OnlineUserManager";
// NOTE: no static import of "@/i18n" here. i18n.js statically imports this
// module AND calls ProfileData.getLanguage() at its own top level, so a
// static edge back to i18n is a cycle that breaks whichever module evaluates
// first (TDZ: "Cannot access 'ProfileData' before initialization").
// setLanguage() below resolves i18n lazily, when both modules are ready.

export default class ProfileData {

    // ##############################################################################################
    // # --------------------------------------- page setting --------------------------------------#
    // ##############################################################################################
    public static readonly pageSizeOptions = [
        {key: "10", value: 1},
        {key: "25", value: 2},
        {key: "50", value: 3},
        {key: "100", value: 4},
    ];

    public static getPageSize(): number {
        return OnlineUserManager.Instance.get().userInfo.profileSetting.pageSize;
    }

    public static getPageSizeKey(): string | undefined {
        return ProfileData.pageSizeOptions
            .find(item => item.value === ProfileData.getPageSize())?.key;
    }

    public static setPageSize(val: number): void {
        OnlineUserManager.Instance.get().userInfo.profileSetting.pageSize = val;
    }

    // ##############################################################################################
    // # ------------------------------------- language setting ------------------------------------#
    // ##############################################################################################
    public static getLanguage(): number {
        return OnlineUserManager.Instance.get().userInfo.profileSetting.language
    }

    public static setLanguage(val: number): void {
        OnlineUserManager.Instance.get().userInfo.profileSetting.language = val;
        // Lazy: keeps the ProfileData <-> i18n edge out of module-eval time.
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const {i18n, setCurrentLanguage} = require("@/i18n");
        setCurrentLanguage(i18n, val);
    }

    // ##############################################################################################
    // # ----------------------------------- appearance setting ------------------------------------#
    // ##############################################################################################
    public static readonly _ValueThemeOs: number = 0;
    public static readonly _ValueThemeLight: number = 1;
    public static readonly _ValueThemeDark: number = 2;

    public static getAppearance() {
        return OnlineUserManager.Instance.get().userInfo.profileSetting.appearance
    }

    public static setAppearance(val: number): string {
        OnlineUserManager.Instance.get().userInfo.profileSetting.appearance = val;
        ProfileData.setTheme(val);
        let themeStr: string;
        if (val === this._ValueThemeOs) {
            themeStr = window.matchMedia("(prefers-color-scheme: dark)").matches ? 'dark' : 'light';
        } else {
            themeStr = val === this._ValueThemeDark ? 'dark' : 'light';
        }
        // ProfileData.setThemeValue(themeStr);
        return themeStr;
    }

    public static applyResolvedAppearance(resolved: "light" | "dark"): void {
        document.documentElement.setAttribute("data-theme", resolved);
        document.body.classList.remove("light", "dark");
        document.body.classList.add(resolved);
    }

    public static resolveAppearance(theme: number): "light" | "dark" {
        if (theme === this._ValueThemeOs) {
            return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
        }
        return theme === this._ValueThemeDark ? "dark" : "light";
    }

    public static setTheme(theme: number): void {
        this.detachOsListener();
        this.applyResolvedAppearance(this.resolveAppearance(theme));
        if (theme === this._ValueThemeOs) {
            this.attachOsListener();
        }
    }

    private static osMedia: MediaQueryList | null = null;
    private static osHandler: ((event: MediaQueryListEvent) => void) | null = null;

    private static attachOsListener(): void {
        this.osMedia = window.matchMedia("(prefers-color-scheme: dark)");
        this.osHandler = (event: MediaQueryListEvent) => {
            this.applyResolvedAppearance(event.matches ? "dark" : "light");
        };
        this.osMedia.addEventListener("change", this.osHandler);
    }

    private static detachOsListener(): void {
        if (this.osMedia && this.osHandler) {
            this.osMedia.removeEventListener("change", this.osHandler);
        }
        this.osMedia = null;
        this.osHandler = null;
    }

    public static initTheme(): void {
        const appearance = OnlineUserManager.Instance.get().userInfo.profileSetting.appearance;
        this.setTheme(appearance ?? this._ValueThemeLight);
        this.setAccent(this.getAccent());
    }

    // ##############################################################################################
    // # ------------------------------------- accent setting --------------------------------------#
    // ##############################################################################################
    public static readonly DefaultAccent = "stronggate";

    public static readonly accentThemes: ReadonlyArray<{ id: string; swatch: string }> = [
        {id: "stronggate", swatch: "#2354A2"},
        {id: "azure", swatch: "#1A73E8"},
        {id: "indigo", swatch: "#6750A4"},
        {id: "teal", swatch: "#00796B"},
        {id: "forest", swatch: "#2E7D32"},
        {id: "sunset", swatch: "#F9A825"},
        {id: "rose", swatch: "#C2185B"},
        {id: "lavender", swatch: "#7E57C2"},
        {id: "slate", swatch: "#5F6368"},
        {id: "spearmint", swatch: "#0F9D91"},
    ];

    public static normalizeAccent(id: string | null | undefined): string {
        return this.accentThemes.some(item => item.id === id) ? id as string : this.DefaultAccent;
    }

    public static getAccent(): string {
        return this.normalizeAccent(
            OnlineUserManager.Instance.get().userInfo.profileSetting.accent
        );
    }

    public static setAccent(id: string): string {
        const accent = this.normalizeAccent(id);
        OnlineUserManager.Instance.get().userInfo.profileSetting.accent = accent;
        this.accentThemes.forEach(item => {
            document.body.classList.remove(`accent-${item.id}`);
        });
        document.body.classList.add(`accent-${accent}`);
        return accent;
    }

    // public static setThemeValue(themeValue: string): void {
    //     CookieUtil.setRawCookie(_KeyTheme, themeValue);
    // }
}